import { useEffect, useRef } from 'react';
import { CallDirection, CallStatus } from '../../modules/call-manager/typings';

interface UseOutgoingCallRingtoneProps {
  direction: CallDirection;
  status: CallStatus;
}

const useOutgoingCallRingtone = ({ direction, status }: UseOutgoingCallRingtoneProps) => {
  const outgoingRingAudioRef = useRef<HTMLAudioElement | null>(null);

  const stopOutgoingRingtone = () => {
    const audio = outgoingRingAudioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        // Ignore errors when stopping audio
      }
    }
  };

  // Create audio element on mount
  useEffect(() => {
    if (!outgoingRingAudioRef.current) {
      const audio = new Audio('/ringing-tune.mp3');
      audio.preload = 'auto';
      // Helps iOS/Safari
      // @ts-expect-error playsInline is not in the TS defs for HTMLAudioElement
      audio.playsInline = true;
      outgoingRingAudioRef.current = audio;
    }

    // Cleanup on unmount
    return () => {
      stopOutgoingRingtone();
    };
  }, []);

  // Outgoing ringback tone: play only when OUTGOING and Ringing
  useEffect(() => {
    const audio = outgoingRingAudioRef.current;
    if (!audio) return;

    if (direction === CallDirection.OUTGOING && status === CallStatus.Ringing) {
      audio.loop = true;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((error) => {
          console.error('Failed to play outgoing ringtone:', error);
        });
      }
    } else {
      stopOutgoingRingtone();
    }
  }, [direction, status]);

  return {
    stopOutgoingRingtone,
  };
};

export default useOutgoingCallRingtone;
